import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import React, { useId } from 'react';

export interface IStatSectionMetric {
  /** Trailing label; also the React key, so unique within a card. */
  label: string;
  /** Rendered before the label; may be a link. */
  value: React.ReactNode;
}

export interface IStatSectionCardProps {
  title: string;
  value: string;
  valueLabel: string;
  hint?: string;
  metrics?: IStatSectionMetric[];
  action?: React.ReactNode;
  loading?: boolean;
  children?: React.ReactNode;
}

/** @registry Titled bordered section for a dashboard: one large headline value with its label, an inline row of secondary "value label" metrics, then any chart or list as children; use instead of a wall of StatTiles when metrics group by topic. */
export const StatSectionCard: React.FC<IStatSectionCardProps> = ({
  title,
  value,
  valueLabel,
  hint,
  metrics = [],
  action,
  loading,
  children,
}) => {
  const titleId = useId();

  return (
    <Paper
      p="md"
      withBorder
      component="section"
      aria-labelledby={titleId}
      h="100%"
    >
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
          <Title order={4} id={titleId}>
            {title}
          </Title>
          {action}
        </Group>
        {loading ? (
          <Loader size="sm" />
        ) : (
          <Stack gap="xs">
            <Group gap="xs" align="baseline" wrap="wrap">
              <Text fz={32} fw={700} lh={1.1} c="bright">
                {value}
              </Text>
              <Text size="md" c="gray.7">
                {valueLabel}
              </Text>
            </Group>
            {hint && (
              <Text size="sm" c="gray.7">
                {hint}
              </Text>
            )}
            {metrics.length > 0 && (
              <Group
                gap="lg"
                style={{ rowGap: 'var(--mantine-spacing-xs)' }}
                data-testid="stat-section-metrics"
              >
                {metrics.map((metric) => (
                  <Text key={metric.label} size="sm" c="gray.7">
                    <Text span size="sm" fw={600} c="bright">
                      {metric.value}
                    </Text>{' '}
                    {metric.label}
                  </Text>
                ))}
              </Group>
            )}
          </Stack>
        )}
        {children}
      </Stack>
    </Paper>
  );
};
